import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { Box, Check, ImagePlus, Loader2, LogOut, Paperclip, Trash2 } from "lucide-react";
import "@/App.css";

export default function OwnerDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(location.state?.user ? true : null);
  const [tab, setTab] = useState("pairs");
  const [pairs, setPairs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [siteImages, setSiteImages] = useState({});
  const [pairTitle, setPairTitle] = useState("");
  const [realFile, setRealFile] = useState(null);
  const [printFile, setPrintFile] = useState(null);
  const [heroFile, setHeroFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [openOrder, setOpenOrder] = useState(null);

  useEffect(() => {
    if (location.state?.user) return;
    api.get("/auth/me")
      .then((r) => { setUser(r.data); setAuthState(true); })
      .catch(() => { setAuthState(false); navigate("/owner", { replace: true }); });
  }, [location.state, navigate]);

  const loadPairs = () => api.get("/pairs").then((r) => setPairs(r.data)).catch(() => {});
  const loadOrders = () => api.get("/owner/orders").then((r) => setOrders(r.data)).catch(() => {});
  const loadSiteImages = () => api.get("/site-images").then((r) => setSiteImages(r.data)).catch(() => {});

  useEffect(() => {
    if (authState === true) { loadPairs(); loadOrders(); loadSiteImages(); }
  }, [authState]);

  const flash = (text, error = false) => {
    setNotice({ text, error });
    setTimeout(() => setNotice(null), 4000);
  };

  const validImage = (f) => f && f.size <= 10 * 1024 * 1024;

  const submitPair = async (e) => {
    e.preventDefault();
    if (!realFile || !printFile) return flash("Choose both photos — the original and the printed statue.", true);
    if (!validImage(realFile) || !validImage(printFile)) return flash("Images must be under 10 MB.", true);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", pairTitle);
      fd.append("real_image", realFile);
      fd.append("print_image", printFile);
      await api.post("/owner/pairs", fd);
      setPairTitle(""); setRealFile(null); setPrintFile(null);
      flash("Comparison published on the site.");
      loadPairs();
    } catch (err) {
      flash(err.response?.data?.detail || "Upload failed. Please try again.", true);
    }
    setBusy(false);
  };

  const removePair = async (id) => {
    try {
      await api.delete(`/owner/pairs/${id}`);
      flash("Comparison removed.");
      loadPairs();
    } catch { flash("Could not remove the comparison.", true); }
  };

  const uploadHero = async () => {
    if (!heroFile) return flash("Choose a photo first.", true);
    if (!validImage(heroFile)) return flash("Images must be under 10 MB.", true);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", heroFile);
      const res = await api.post("/owner/site-images/hero", fd);
      setSiteImages({ ...siteImages, hero: res.data.file_id });
      setHeroFile(null);
      flash("Hero photo updated — it is live on the page now.");
    } catch (err) {
      flash(err.response?.data?.detail || "Upload failed. Please try again.", true);
    }
    setBusy(false);
  };

  const toggleStatus = async (order) => {
    const next = order.status === "new" ? "contacted" : "new";
    try {
      await api.patch(`/owner/orders/${order.id}`, { status: next });
      loadOrders();
    } catch { flash("Could not update the status.", true); }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } finally { navigate("/owner"); }
  };

  if (authState !== true) {
    return <div className="site-shell loading-screen" data-testid="dashboard-loading"><Loader2 className="spin" size={30} /></div>;
  }

  const paidCount = orders.filter((o) => o.payment_status === "paid" && o.status === "new").length;

  return (
    <div className="site-shell dash" data-testid="owner-dashboard">
      <div className="noise" aria-hidden="true" />
      <nav className="dash-nav" data-testid="dashboard-nav">
        <Link to="/" className="brand" data-testid="dashboard-brand-link"><span className="brand-mark"><Box size={18} strokeWidth={2.5} /></span><span>Biswas<span className="cyan">3D</span></span></Link>
        <div className="dash-user">
          {user?.picture && <img src={user.picture} alt="" referrerPolicy="no-referrer" />}
          <span data-testid="dashboard-user-email">{user?.email}</span>
          <button className="icon-button" data-testid="owner-logout-button" onClick={logout}><LogOut size={14} /> Log out</button>
        </div>
      </nav>
      <main className="dash-main">
        <div className="dash-tabs">
          <button className={`dash-tab ${tab === "pairs" ? "active" : ""}`} data-testid="tab-pairs-button" onClick={() => setTab("pairs")}>Comparisons</button>
          <button className={`dash-tab ${tab === "site" ? "active" : ""}`} data-testid="tab-site-button" onClick={() => setTab("site")}>Hero photo</button>
          <button className={`dash-tab ${tab === "orders" ? "active" : ""}`} data-testid="tab-orders-button" onClick={() => setTab("orders")}>Orders{paidCount > 0 ? ` (${paidCount})` : ""}</button>
        </div>
        {notice && <p className={`dash-notice ${notice.error ? "error" : ""}`} data-testid="dashboard-notice">{notice.text}</p>}

        {tab === "pairs" && (
          <section data-testid="pairs-panel">
            <h1 className="panel-title">Photo vs printed comparisons</h1>
            <p className="panel-sub">Upload the customer's original photo next to the finished statue — this is your proof of accuracy on the site.</p>
            <form className="upload-card" data-testid="pair-upload-form" onSubmit={submitPair}>
              <div className="field-row">
                <label className="file-drop" data-testid="pair-real-drop">
                  <ImagePlus size={18} />
                  <span>{realFile ? <strong>{realFile.name}</strong> : "1. Original photo of the person"}</span>
                  <input type="file" accept="image/*" hidden data-testid="pair-real-input" onChange={(e) => setRealFile(e.target.files?.[0] || null)} />
                </label>
                <label className="file-drop" data-testid="pair-print-drop">
                  <ImagePlus size={18} />
                  <span>{printFile ? <strong>{printFile.name}</strong> : "2. Photo of the printed statue"}</span>
                  <input type="file" accept="image/*" hidden data-testid="pair-print-input" onChange={(e) => setPrintFile(e.target.files?.[0] || null)} />
                </label>
              </div>
              <div className="field"><label htmlFor="pair-title">Title</label><input id="pair-title" data-testid="pair-title-input" value={pairTitle} onChange={(e) => setPairTitle(e.target.value)} placeholder="e.g. Anniversary couple, 20 cm" /></div>
              <button className="button button-primary submit-button" data-testid="pair-upload-submit-button" type="submit" disabled={busy}>
                {busy ? <Loader2 size={16} className="spin" /> : <ImagePlus size={16} />} Publish comparison
              </button>
            </form>
            {pairs.length === 0 ? (
              <div className="empty-state" data-testid="pairs-empty-state">No comparisons yet. Upload a photo and its printed statue side by side.</div>
            ) : (
              <div className="dash-gallery" data-testid="dashboard-pairs-grid">
                {pairs.map((p) => (
                  <div className="dash-item" key={p.id} data-testid={`pair-item-${p.id}`}>
                    <div className="pair-thumb">
                      <img src={`${API}/files/${p.real_file_id}`} alt={`${p.title} original`} />
                      <img src={`${API}/files/${p.print_file_id}`} alt={`${p.title} printed`} />
                    </div>
                    <div className="dash-item-body">
                      <strong>{p.title}</strong>
                      <div className="dash-item-actions">
                        <button className="icon-button danger" data-testid={`pair-delete-${p.id}`} onClick={() => removePair(p.id)}><Trash2 size={13} /> Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "site" && (
          <section data-testid="site-images-panel">
            <h1 className="panel-title">Hero photo</h1>
            <p className="panel-sub">The big photo at the top of the homepage. Choose a photo, then Save.</p>
            <div className="dash-gallery hero-slot-grid">
              <div className="dash-item" data-testid="site-slot-hero">
                {siteImages.hero ? (
                  <img src={`${API}/files/${siteImages.hero}`} alt="Homepage hero" />
                ) : (
                  <div className="img-placeholder slot-empty"><Box size={26} strokeWidth={1.5} /><span>No photo yet</span></div>
                )}
                <div className="dash-item-body">
                  <strong>Hero — main page photo</strong>
                  <div className="dash-item-actions">
                    <label className="icon-button" data-testid="site-upload-label-hero">
                      <ImagePlus size={13} /> {heroFile ? heroFile.name.slice(0, 20) : "Choose photo"}
                      <input type="file" accept="image/*" hidden data-testid="site-upload-input-hero" onChange={(e) => setHeroFile(e.target.files?.[0] || null)} />
                    </label>
                    <button className="icon-button" data-testid="site-upload-save-hero" disabled={busy} onClick={uploadHero}>
                      {busy ? <Loader2 size={13} className="spin" /> : <Check size={13} />} Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {tab === "orders" && (
          <section data-testid="orders-panel">
            <h1 className="panel-title">Orders</h1>
            <p className="panel-sub">Paid orders are ready to fulfil. Call the customer to confirm photos and details, then mark it contacted.</p>
            {orders.length === 0 ? (
              <div className="empty-state" data-testid="orders-empty-state">No orders yet.</div>
            ) : (
              <div className="orders-list" data-testid="orders-list">
                {orders.map((o) => (
                  <div className="order-row" key={o.id} data-testid={`order-row-${o.id}`}>
                    <button className="order-row-head" data-testid={`order-toggle-${o.id}`} onClick={() => setOpenOrder(openOrder === o.id ? null : o.id)}>
                      <span className="cell-name"><strong>{o.customer_name}</strong><span>{new Date(o.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span></span>
                      <span className="cell-dim">{o.phone}</span>
                      <span className="cell-dim">{o.product_name} &middot; {o.size_cm} cm &middot; &#8377;{(o.amount / 100).toLocaleString("en-IN")}</span>
                      <span className={`status-badge status-${o.payment_status === "paid" ? "paid" : "awaiting"}`} data-testid={`order-payment-${o.id}`}>{o.payment_status === "paid" ? "Paid" : "Awaiting"}</span>
                      <span className="cell-dim">{openOrder === o.id ? "Hide" : "View"}</span>
                    </button>
                    {openOrder === o.id && (
                      <div className="order-detail" data-testid={`order-detail-${o.id}`}>
                        <div><h4>Order</h4><p>{o.product_name} statue &middot; {o.size_cm} cm &middot; PLA<br />Amount: &#8377;{(o.amount / 100).toLocaleString("en-IN")}{o.razorpay_payment_id ? <><br />Payment ID: {o.razorpay_payment_id}</> : null}</p></div>
                        <div><h4>Contact &amp; address</h4><p>{o.phone}{o.customer_email ? <><br />{o.customer_email}</> : null}<br />{o.address}</p></div>
                        <div>
                          <h4>Customer photos</h4>
                          <div className="attachment-links">
                            {(o.attachments || []).map((a) => <a key={a.file_id} data-testid={`order-attachment-${a.file_id}`} href={`${API}/files/${a.file_id}`} target="_blank" rel="noreferrer"><Paperclip size={13} /> {a.name}</a>)}
                          </div>
                        </div>
                        <div>
                          <h4>Actions</h4>
                          <div className="dash-item-actions">
                            <a className="icon-button" data-testid={`order-whatsapp-${o.id}`} href={`https://wa.me/91${o.phone.replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noreferrer">WhatsApp customer</a>
                            <button className="icon-button" data-testid={`order-status-toggle-${o.id}`} onClick={() => toggleStatus(o)}>{o.status === "new" ? "Mark contacted" : "Mark new"}</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
